'use client';

import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { moveLibraryItem, type LibraryFolder } from '@/lib/library-workspace';
import { LIBRARY_DRAG_TYPE, matchesLibraryDrag, type LibraryDragItem } from '@/lib/library-drag';
import { useSuccessToast } from './ToastProvider';

type DragContext = {
  active: LibraryDragItem | null;
  moving: boolean;
  start: (item: LibraryDragItem) => void;
  end: () => void;
  move: (raw: string, folderId: string) => Promise<void>;
};
const Context = createContext<DragContext | null>(null);

export function LibraryDragDropProvider({ folders, onMoved, children }: {
  folders: LibraryFolder[];
  onMoved: (item: LibraryDragItem, folderId: string) => void;
  children: ReactNode;
}) {
  const [active, setActive] = useState<LibraryDragItem | null>(null);
  const [moving, setMoving] = useState(false);
  const [error, setError] = useState('');
  const inFlight = useRef(false);
  const mounted = useRef(true);
  const toast = useSuccessToast();
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  async function move(raw: string, folderId: string) {
    if (inFlight.current || !matchesLibraryDrag(raw, active) || !active || active.folderId === folderId) return;
    if (folderId && !folders.some(folder => folder.id === folderId)) return;
    const item = active;
    inFlight.current = true; setMoving(true); setActive(null); setError('');
    try {
      await moveLibraryItem(folderId, item.kind === 'deck' ? { deck: item.id } : { job: item.id });
      if (!mounted.current) return;
      onMoved(item, folderId);
      toast(`Moved to ${folders.find(folder => folder.id === folderId)?.name || 'Main'}`);
    } catch (reason) {
      if (mounted.current) setError(reason instanceof Error ? reason.message : 'Could not move this lecture. Please try again.');
    } finally { inFlight.current = false; if (mounted.current) setMoving(false); }
  }
  return <Context.Provider value={{ active, moving, move, start: item => { if (!inFlight.current) { setActive(item); setError(''); } }, end: () => setActive(null) }}>
    {error && <p role="alert" className="mb-3 text-sm text-danger">{error}</p>}
    <span role="status" className="sr-only">{moving ? 'Moving lecture…' : active ? 'Drop onto a folder or Main. Press Escape to cancel.' : ''}</span>
    {children}
  </Context.Provider>;
}

export function LibraryDraggable({ item, disabled = false, className, children, as: Tag = 'div' }: {
  item: LibraryDragItem;
  disabled?: boolean;
  className?: string;
  children: ReactNode;
  as?: 'div' | 'article';
}) {
  const context = useContext(Context);
  const suppressClick = useRef(false);
  const resetTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => () => clearTimeout(resetTimer.current), []);
  const isDragging = context?.active?.kind === item.kind && context.active.id === item.id;
  return <Tag draggable={!!context && !disabled && !context.moving} data-library-item={item.kind}
    className={`${className || ''} ${isDragging ? 'opacity-50' : ''}`}
    onDragStart={event => {
      if (!context || disabled || context.moving) { event.preventDefault(); return; }
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData(LIBRARY_DRAG_TYPE, JSON.stringify(item));
      clearTimeout(resetTimer.current); suppressClick.current = true;
      context.start(item);
    }}
    onDragEnd={() => { context?.end(); resetTimer.current = setTimeout(() => { suppressClick.current = false; }, 0); }}
    onClickCapture={event => { if (suppressClick.current) { event.preventDefault(); event.stopPropagation(); } }}>
    {children}
  </Tag>;
}

export function LibraryFolderDropTarget({ folderId, disabled = false, className, children }: {
  folderId: string;
  disabled?: boolean;
  className?: string;
  children: ReactNode;
}) {
  const context = useContext(Context);
  const [over, setOver] = useState(false);
  const allowed = !!context?.active && !context.moving && !disabled && context.active.folderId !== folderId;
  useEffect(() => { if (!context?.active) setOver(false); }, [context?.active]);
  return <div className={`${className || ''} rounded-md ${allowed && over ? 'bg-primary/15 ring-2 ring-inset ring-primary' : ''}`}
    data-folder-drop={disabled ? undefined : folderId || 'main'}
    onDragOver={event => {
      if (!allowed || !event.dataTransfer.types.includes(LIBRARY_DRAG_TYPE)) return;
      event.preventDefault(); event.dataTransfer.dropEffect = 'move'; setOver(true);
    }}
    onDragLeave={event => { if (!(event.relatedTarget instanceof Node) || !event.currentTarget.contains(event.relatedTarget)) setOver(false); }}
    onDrop={event => {
      if (!allowed) return;
      event.preventDefault(); event.stopPropagation(); setOver(false);
      void context?.move(event.dataTransfer.getData(LIBRARY_DRAG_TYPE), folderId);
    }}>
    {children}
  </div>;
}
